import { useMemo } from "react";
import { useQuery } from "react-query";
import axios from "../config/axiosInstance";
import { ADMIN } from "../config/apiConfig";

const EMPTY = {
  plans: [],
  planCycles: [],
  segments: [],
  companyTypes: [],
  companies: [],
  teams: [],
  permissions: [],
  cities: [],
  salesModels: [],
  sizes: [],
  ticketBands: [],
  aiProviders: [],
  voipProviders: [],
  intervalMonths: [],
};

const toOptions = (list = [], labelKey = "name") =>
  list.map((item) => ({
    value: String(item.id),
    label: item[labelKey] || item.code || `#${item.id}`,
  }));

const toPlainOptions = (list = []) =>
  list.map((item) => ({ value: String(item), label: String(item) }));

/**
 * Catálogo compartilhado que alimenta todos os selects de filtro do
 * back-office. Uma requisição só, cacheada, reaproveitada entre as telas.
 */
const useAdminFilterOptions = ({ enabled = true } = {}) => {
  const { data, isLoading, error } = useQuery(
    ["admin-filter-options"],
    async () => {
      const response = await axios.get(ADMIN.GET_FILTER_OPTIONS);
      return response.data?.data || EMPTY;
    },
    { enabled, staleTime: 10 * 60 * 1000, keepPreviousData: true }
  );

  const raw = data || EMPTY;

  const options = useMemo(
    () => ({
      plans: toOptions(raw.plans),
      segments: raw.segments.map((segment) => ({
        value: String(segment.id),
        label: segment.emoji ? `${segment.emoji} ${segment.name}` : segment.name,
      })),
      companyTypes: raw.companyTypes.map((type) => ({
        value: String(type.id),
        label: type.name,
      })),
      companies: raw.companies.map((company) => ({
        value: String(company.id),
        label: company.isFilial ? `${company.label} (filial)` : company.label,
      })),
      teams: raw.teams.map((team) => ({
        value: String(team.id),
        label: team.name,
      })),
      planCycles: raw.planCycles.map((cycle) => ({
        value: String(cycle.id),
        label:
          cycle.description ||
          `${cycle.intervalMonths} ${
            Number(cycle.intervalMonths) === 1 ? "mês" : "meses"
          }`,
      })),
      intervalMonths: raw.intervalMonths.map((months) => ({
        value: String(months),
        label:
          months === 1
            ? "Mensal"
            : months === 3
            ? "Trimestral"
            : months === 12
            ? "Anual"
            : `${months} meses`,
      })),
      permissions: toPlainOptions(raw.permissions),
      cities: toPlainOptions(raw.cities),
      salesModels: toPlainOptions(raw.salesModels),
      sizes: toPlainOptions(raw.sizes),
      ticketBands: toPlainOptions(raw.ticketBands),
      aiProviders: toPlainOptions(raw.aiProviders),
      voipProviders: toPlainOptions(raw.voipProviders),
    }),
    [raw]
  );

  return { options, raw, isLoading, error };
};

export default useAdminFilterOptions;
